import React, { useState } from 'react';
import {
  DetailsList,
  DetailsRow,
  DetailsHeader,
  Selection,
  SelectionMode,
} from 'office-ui-fabric-react';
import { useTranslation } from 'react-i18next';
import { HearingStatusTag } from '../../../../components/HearingStatusTag';
import { HearingElementGrid } from './hearingList/HearingElementGrid';
import { SORTING_KEYS } from '../../../../constants/sortingKeys';
import { filterAndSortingKeys } from '../../../../utilities/filterAndSortingsUtil';

let listStyle = {
  root: {
    backgroundColor: '#FAFAFA',
  },
};

const HearingList = ({
  items,
  showGrid,
  setFilterAndSortingSelection,
  filterAndSortingSelection,
  disableEsdh,
  disableOwner,
  sortingKey,
  onHearingClick,
  onHearingSelect,
  disableSelection,
}) => {
  const { t: translator } = useTranslation();

  const onColumnHeaderClick = (_, col) => {
    let resultingSortingKey = null;

    if (col.key === 'title') {
      if (sortingKey === SORTING_KEYS.ALPHABETICAL) {
        resultingSortingKey = SORTING_KEYS.ALPHABETICAL_REVERSE;
      } else {
        resultingSortingKey = SORTING_KEYS.ALPHABETICAL;
      }
    } else if (col.key === 'startDate') {
      if (sortingKey === SORTING_KEYS.RELEASE_SOONEST) {
        resultingSortingKey = SORTING_KEYS.RELEASE_LATEST;
      } else {
        resultingSortingKey = SORTING_KEYS.RELEASE_SOONEST;
      }
    } else if (col.key === 'deadline') {
      if (sortingKey === SORTING_KEYS.DEADLINE_SOONEST) {
        resultingSortingKey = SORTING_KEYS.DEADLINE_LATEST;
      } else {
        resultingSortingKey = SORTING_KEYS.DEADLINE_SOONEST;
      }
    }

    if (resultingSortingKey) {
      let currFilterAndSortingSelection = Object.assign(
        {},
        filterAndSortingSelection,
      );
      currFilterAndSortingSelection[
        filterAndSortingKeys.sortingKey
      ] = resultingSortingKey;
      setFilterAndSortingSelection(currFilterAndSortingSelection);
    }
  };

  const onSelectionChanged = () => {
    const [selection] = listSelection.getSelection();
    onHearingSelect && onHearingSelect(selection && selection.key);
  };

  const [listSelection] = useState(
    new Selection({
      onSelectionChanged: onSelectionChanged,
      selectionMode: disableSelection
        ? SelectionMode.none
        : SelectionMode.single,
    }),
  );

  const selectionKey =
    items && items.length
      ? items
          .map((i) => i.key)
          .sort()
          .join('')
      : 'none';

  return (
    <div className="changeHearingOwner-list">
      {showGrid ? (
        <HearingElementGrid
          hearingItems={items}
          onHearingClick={onHearingClick}
        />
      ) : (
        <DetailsList
          items={items}
          ariaLabel={translator('ariaLabel.hearingListLabel')}
          ariaLabelForSelectionColumn={translator(
            'ariaLabel.listSelectionColumn',
          )}
          ariaLabelForSelectAllCheckbox={translator('ariaLabel.listSelectAll')}
          selection={listSelection}
          selectionMode={
            disableSelection ? SelectionMode.none : SelectionMode.single
          }
          setKey={selectionKey}
          isCompactMode={false}
          columns={columns(translator, disableEsdh, disableOwner, sortingKey)}
          onRenderDetailsHeader={(props) => {
            return (
              <DetailsHeader
                {...props}
                styles={listStyle}
                ariaLabel={translator('ariaLabel.listHeader')}
                tabIndex="0"
              />
            );
          }}
          onColumnHeaderClick={onColumnHeaderClick}
          onRenderRow={(props) => {
            return <DetailsRow tabIndex="0" {...props} styles={listStyle} />;
          }}
          onItemInvoked={onHearingClick}
        />
      )}
    </div>
  );
};

export { HearingList };

const columns = (translator, disableEsdh, disableOwner, sortingKey) => [
  {
    className: 'title-column',
    key: 'title',
    name: translator('hearingProperties.title'),
    ariaLabel: translator('hearingProperties.title'),
    fieldName: 'title',
    data: 'string',
    isPadded: true,
    isResizable: false,
    isMultiline: true,
    minWidth: 350,
    isSorted:
      sortingKey === SORTING_KEYS.ALPHABETICAL ||
      sortingKey === SORTING_KEYS.ALPHABETICAL_REVERSE,
    isSortedDescending: sortingKey === SORTING_KEYS.ALPHABETICAL,
    sortAscendingAriaLabel: translator('ariaLabel.sortedAscending'),
    sortDescendingAriaLabel: translator('ariaLabel.sortedDescending'),
  },
  {
    key: 'hearingStatus',
    name: translator('hearingProperties.hearingStatus'),
    ariaLabel: translator('hearingProperties.hearingStatus'),
    fieldName: 'hearingStatus',
    data: 'number',
    minWidth: 140,
    onRender: (item) => {
      return (
        <div>
          <HearingStatusTag
            status={item.hearingStatus}
            translator={translator}
          />
        </div>
      );
    },
    isSorted: false,
    sortAscendingAriaLabel: translator('ariaLabel.sortedAscending'),
    sortDescendingAriaLabel: translator('ariaLabel.sortedDescending'),
  },
  {
    key: 'hearingType',
    name: translator('hearingProperties.hearingType'),
    ariaLabel: translator('hearingProperties.hearingType'),
    fieldName: 'hearingType',
    isPadded: true,
    minWidth: 130,
    isSorted: false,
    sortAscendingAriaLabel: translator('ariaLabel.sortedAscending'),
    sortDescendingAriaLabel: translator('ariaLabel.sortedDescending'),
  },
  {
    key: 'cityArea',
    name: translator('hearingProperties.cityArea'),
    ariaLabel: translator('hearingProperties.cityArea'),
    fieldName: 'cityArea',
    isPadded: true,
    minWidth: 121,
    isSorted: false,
    sortAscendingAriaLabel: translator('ariaLabel.sortedAscending'),
    sortDescendingAriaLabel: translator('ariaLabel.sortedDescending'),
  },
  {
    key: 'subjectArea',
    name: translator('hearingProperties.subjectArea'),
    ariaLabel: translator('hearingProperties.subjectArea'),
    fieldName: 'subjectArea',
    isPadded: true,
    minWidth: 121,
    isSorted: false,
    sortAscendingAriaLabel: translator('ariaLabel.sortedAscending'),
    sortDescendingAriaLabel: translator('ariaLabel.sortedDescending'),
  },
  {
    key: 'startDate',
    name: translator('hearingProperties.startDate'),
    ariaLabel: translator('hearingProperties.startDate'),
    fieldName: 'startDate',
    onRender: (item) => {
      return <span>{item.startDate}</span>;
    },
    minWidth: 100,
    isSorted:
      sortingKey === SORTING_KEYS.RELEASE_LATEST ||
      sortingKey === SORTING_KEYS.RELEASE_SOONEST,
    isSortedDescending: sortingKey === SORTING_KEYS.RELEASE_SOONEST,
    sortAscendingAriaLabel: translator('ariaLabel.sortedAscending'),
    sortDescendingAriaLabel: translator('ariaLabel.sortedDescending'),
  },
  {
    key: 'deadline',
    name: translator('hearingProperties.deadlineDate'),
    ariaLabel: translator('hearingProperties.deadlineDate'),
    fieldName: 'deadline',
    onRender: (item) => {
      return <span>{item.deadline}</span>;
    },
    minWidth: 100,
    isSorted:
      sortingKey === SORTING_KEYS.DEADLINE_LATEST ||
      sortingKey === SORTING_KEYS.DEADLINE_SOONEST,
    isSortedDescending: sortingKey === SORTING_KEYS.DEADLINE_SOONEST,
    sortAscendingAriaLabel: translator('ariaLabel.sortedAscending'),
    sortDescendingAriaLabel: translator('ariaLabel.sortedDescending'),
  },
  {
    key: 'responses',
    name: translator('hearingProperties.numberOfComments'),
    ariaLabel: translator('hearingProperties.numberOfComments'),
    fieldName: 'responses',
    isPadded: true,
    minWidth: 100,
    isSorted: false,
  },
  ...(!disableOwner
    ? [
        {
          key: 'hearingOwner',
          name: translator('hearingProperties.hearingOwner'),
          ariaLabel: translator('hearingProperties.hearingOwner'),
          fieldName: 'hearingOwner',
          isPadded: true,
          minWidth: 150,
          isSorted: false,
          sortAscendingAriaLabel: translator('ariaLabel.sortedAscending'),
          sortDescendingAriaLabel: translator('ariaLabel.sortedDescending'),
        },
      ]
    : []),
  ...(!disableEsdh
    ? [
        {
          key: 'esdhNumber',
          name: translator('global.esdhSystem'),
          ariaLabel: translator('global.esdhSystem'),
          fieldName: 'esdhNumber',
          isPadded: true,
          minWidth: 150,
          isSorted: false,
          sortAscendingAriaLabel: translator('ariaLabel.sortedAscending'),
          sortDescendingAriaLabel: translator('ariaLabel.sortedDescending'),
        },
      ]
    : []),
];
